<script lang="ts">
  import { ChevronDownIcon } from '@lucide/svelte'
  import SelectOption from './SelectOption.svelte'
  import Button from '../button/Button.svelte'

  export type SelectContext = {
    value?: string
    open: boolean
  }

  type Props<T = unknown> = {
    name: string
    options: {
      label: string
      value: T
    }[]
    value?: T
    placeholder?: string
    onchange?: (value: T) => void
  }

  let {
    options,
    value = $bindable(),
    name,
    placeholder,
    onchange,
  }: Props = $props()
  let selectedOption = $derived(options.find(option => option.value === value))
  let isOpen = $state(false)

  let container: HTMLDivElement
  window.addEventListener('click', function (e) {
    if (!(e && container && container.contains(e.target as Node))) {
      isOpen = false
    }
  })
</script>

<div bind:this={container} class={['container', isOpen ? 'opened' : '']}>
  <input {name} type="hidden" {value} />
  <Button
    onclick={() => {
      isOpen = !isOpen
    }}
    size="sm"
    variant="secondary"
  >
    <span>{selectedOption?.label ?? placeholder}</span>
    <ChevronDownIcon />
  </Button>
  <div class="options">
    {#each options as option (option.value)}
      <SelectOption
        onclick={() => {
          value = option.value
          onchange?.(option.value)
          isOpen = false
        }}
        selected={value === option.value}>{option.label}</SelectOption
      >
    {/each}
  </div>
</div>

<style lang="scss">
  .container {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    width: 100%;

    span {
      color: var(--select-foreground);
      font-size: 0.75rem;
      line-height: 150%;
      overflow: hidden;
      text-align: start;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
    }

    :global(button) {
      align-items: center;
      background-color: var(--select-background);
      border-radius: 0.25rem;
      border: none;
      cursor: pointer;
      display: flex;
      justify-content: space-between;
      outline: none;
      overflow: hidden;
      padding: 0.25rem;
      padding-left: 0.5rem;

      &:focus-visible {
        outline: 1px solid var(--stroke);
        outline-offset: 2px;
        z-index: 1;
      }

      :global(svg) {
        height: 1rem;
        width: 1rem;
        stroke: var(--select-foreground);
      }
    }

    &.opened {
      .options {
        display: flex;
      }
    }

    .options {
      position: absolute;
      top: calc(100% + 0.125rem);
      width: 100%;
      display: none;
      flex-direction: column;
      border-radius: 0.25rem;
      background-color: var(--select-background);
      box-shadow: 4px 4px 4px 0px var(--select-shadow-color);
      z-index: 99;
    }
  }
</style>
